/**
 * Zuweisen beim Antworten (User 02.11.2026).
 *
 * Antwortet man im Schriftverkehr auf eine Mail, die noch an KEINEM Vorgang
 * hängt, lässt sich im Antwort-Fenster gleich zuordnen — Kontakt und/oder
 * Vorgang/To-do. Gewählt wird mit den vorhandenen Fenstern („Kontakt
 * zuweisen", „Bestehendem Vorgang zuordnen"); wirksam wird die Wahl erst beim
 * Senden:
 *
 *  1. Die URSPRUNGSMAIL wird zugeordnet — über dieselben Wege wie im
 *     Posteingang (`POST /mail/links/batch` samt Verlauf, `POST
 *     /mail/links/kontakt`), nur ohne Archivieren: die Mail wird gerade
 *     beantwortet, und beim Archivieren vergäbe Graph ihr eine neue Id.
 *  2. Die ANTWORT erbt den Vorgang danach von selbst über die Konversation
 *     (`inheritLinksFromMessageId`, Server `resolveAutoLinkTargets`) — auf
 *     derselben Ebene, also am gewählten To-do. Den Kontakt bekommt sie über
 *     `kontaktId` beim Senden.
 *
 * Reine Logik ohne React — die Leiste steht in `AntwortZuordnungLeiste.tsx`.
 */
import type { KontaktRef } from "./mailAssignedContact";
import type { MailVorgangZiel } from "./mailVorgangAssign";
import type { MailLinkBatchItemInput } from "../../services/mailLinkService";

/** Was das Antwort-Fenster zum Zuordnen braucht — nur gesetzt, wenn es etwas zuzuordnen gibt. */
export type AntwortZuordnungAngebot = {
    /** Schnappschuss der Ursprungsmail; `mailbox` ist ihr Fundort. */
    ursprung: MailLinkBatchItemInput;
    senderEmail: string | null;
    senderName: string | null;
    /** Erkannter Kontakt — Rangstufe 1 der Vorgangsauswahl, nichts wird vorbelegt. */
    erkannterKontakt?: KontaktRef | null;
    /** Hat die Konversation schon einen Kontakt, entfällt dessen Auswahl. */
    kontaktZugewiesen?: KontaktRef | null;
};

export type AntwortZuordnungWahl = {
    kontakt: KontaktRef | null;
    ziele: MailVorgangZiel[];
    /** Lesbare Bezeichnung der Ziele („To-do „Kündigung““), sobald geladen. */
    zieleText: string | null;
};

export const LEERE_WAHL: AntwortZuordnungWahl = { kontakt: null, ziele: [], zieleText: null };

export const hatWahl = (w: AntwortZuordnungWahl): boolean => w.kontakt != null || w.ziele.length > 0;

/** Ein Konversations-Link, so weit die Entscheidung ihn braucht. */
type LinkFuerAngebot = {
    vorgangId?: number | null;
    todoId?: number | null;
    vorgang?: { id: number } | null;
};

/**
 * Bietet das Antwort-Fenster das Zuordnen an? Nur, wenn die Links der
 * Konversation geladen sind (sonst flackerte das Angebot bei einer längst
 * zugeordneten Mail auf) und KEINER davon an einem Vorgang oder To-do hängt —
 * dann erbt die Antwort ihren Vorgang ohnehin selbst.
 */
export function bieteZuordnungAn(links: readonly LinkFuerAngebot[] | null | undefined): boolean {
    if (links == null) return false;
    return !links.some((l) => l.vorgang != null || l.vorgangId != null || l.todoId != null);
}

/**
 * Schritt 1 beim Senden: die Ursprungsmail zuordnen. Erst alle Ziele, dann
 * der Kontakt. Ein Fehler wirft — der Aufrufer sendet dann nicht, damit die
 * Antwort nicht ohne den gewählten Vorgang rausgeht. Rückgabe: Hinweise aus
 * dem Verlauf (Teilfehler, gekappte Überlänge), sonst `null`.
 */
export async function ordneUrsprungZu(
    wahl: AntwortZuordnungWahl,
    deps: {
        bindeZiel: (ziel: MailVorgangZiel) => Promise<string | null>;
        verknuepfeKontakt: (contactId: number) => Promise<unknown>;
    },
): Promise<string | null> {
    const meldungen: string[] = [];
    for (const ziel of wahl.ziele) {
        if (ziel.vorgangId == null && ziel.todoId == null) continue;
        const m = await deps.bindeZiel(ziel);
        if (m && !meldungen.includes(m)) meldungen.push(m);
    }
    if (wahl.kontakt) await deps.verknuepfeKontakt(wahl.kontakt.id);
    return meldungen.length ? meldungen.join(" ") : null;
}

/** „To-do „X““ / „Vorgang „Y““ — mehrere mit Komma. */
export function zieleBeschriften(
    ziele: MailVorgangZiel[],
    titel: { vorgaenge: Map<number, string>; todos: Map<number, string> },
): string {
    return ziele
        .map((z) => {
            if (z.todoId != null) {
                const t = titel.todos.get(z.todoId);
                return t ? `To-do „${t}“` : `To-do #${z.todoId}`;
            }
            if (z.vorgangId != null) {
                const t = titel.vorgaenge.get(z.vorgangId);
                return t ? `Vorgang „${t}“` : `Vorgang #${z.vorgangId}`;
            }
            return "";
        })
        .filter(Boolean)
        .join(", ");
}
